import { Component, computed, effect, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Icon } from '@shared/icon/icon';
import { Button } from '@shared/button/button';
import type { IconName, CssColor } from '@appTypes';

type SidebarType = 'filter' | 'list';

@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [CommonModule, Icon, Button],
  templateUrl: './filter-sidebar.html',
  styleUrls: ['./filter-sidebar.scss'],
})
export class FilterSidebar {
  // Inputs
  $type = input<SidebarType>('filter', { alias: 'type' });
  $icon = input<IconName | null>(null, { alias: 'icon' });
  $iconSize = input<string>('2.8rem', { alias: 'iconSize' });
  $iconColor = input<'original' | CssColor>('--white', { alias: 'iconColor' });

  // Computed icon basado en type o icon personalizado
  $computedIcon = computed<IconName>(() => {
    const customIcon = this.$icon();
    if (customIcon) return customIcon;
    return this.$type() === 'list' ? 'format-list-bulleted-type' : 'filter-outline';
  });
  $buttonText = input<string>('Filtrar', { alias: 'buttonText' });
  $buttonIcon = input<IconName | null>('filter-outline', { alias: 'buttonIcon' });
  $showButton = input<boolean>(true, { alias: 'showButton' });
  $sidebarWidth = input<string>('300px', { alias: 'sidebarWidth' });
  $topPosition = input<string>('5rem', { alias: 'topPosition' });
  $leftPosition = input<string>('10rem', { alias: 'leftPosition' });
  $triggerTopPosition = input<string>('15rem', { alias: 'triggerTopPosition' });
  $triggerLeftPosition = input<string>('3rem', { alias: 'triggerLeftPosition' });
  $initialOpen = input<boolean>(true, { alias: 'initialOpen' });
  $isOpen = input<boolean | undefined>(undefined, { alias: 'isOpen' });

  // Outputs
  filterClick = output<void>();
  openChange = output<boolean>();

  // State
  isOpen = signal<boolean>(true);

  constructor() {
    // Sincronizar el estado cuando cambia el input $isOpen
    effect(() => {
      const inputValue = this.$isOpen();
      if (inputValue !== undefined) {
        this.isOpen.set(inputValue);
      }
    });
  }

  toggle(): void {
    this.isOpen.update(v => !v);
    this.openChange.emit(this.isOpen());
  }

  open(): void {
    this.isOpen.set(true);
    this.openChange.emit(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.openChange.emit(false);
  }

  onFilterClick(): void {
    this.filterClick.emit();
  }
}
